import { useQueryClient } from '@tanstack/react-query';

import type { ActivateRecurringMembershipArgs } from '../../api/types/memberships.types';

import { isMembershipTermsRequiredError } from '../../allFeatures/Payments/membershipTermsRequiredError';
import { isNoSavedCardError } from '../../allFeatures/Payments/noSavedCardError';
import { activateRecurringMembership } from '../../api/memberships.api';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';
import { invalidateMembershipDebtViews } from './invalidateMembershipDebtViews';

/**
 * MV2-573 §2: `NO_SAVED_CARD` maps to the wallet card-setup CTA
 * (`usePurchaseWithCardSetup`), not a generic error toast — same contract as
 * `useSubscribePremium`.
 */
export default function useActivateRecurringMembership() {
  const queryClient = useQueryClient();
  const response = useMutation({
    meta: {
      suppressErrorToast: (error: unknown) =>
        isNoSavedCardError(error) || isMembershipTermsRequiredError(error),
    },
    mutationFn: (args: ActivateRecurringMembershipArgs) =>
      activateRecurringMembership(args),
    onSuccess: (_, { clientId, organizationId }) => {
      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.memberships.fetchClientMemberships({
          clientId,
          organizationId,
        }).queryKey,
      });
      invalidateMembershipDebtViews(queryClient);
    },
  });
  return {
    ...response,
    isLoading: response.status === 'pending',
  };
}
